import { get, post, put, del } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const tenderService = {
  // Get tenders with optional filters (status, priority, search) and pagination
  getTenders: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString
        ? `${API_ENDPOINTS.TENDER.LIST}?${queryString}`
        : API_ENDPOINTS.TENDER.LIST;

      const response = await get(url);

      if (response.responseCode === 200 && response.data) {
        // Expecting shape: { items: [...], pagination: {...} }
        const { items, pagination } = response.data;
        return {
          success: true,
          data: Array.isArray(items) ? items : [],
          pagination: pagination || null,
        };
      }

      // Fallback if older API returns array directly
      if (Array.isArray(response.data)) {
        return {
          success: true,
          data: response.data,
          pagination: null,
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to fetch tenders',
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch tenders',
      };
    }
  },

  // Get tender summary stats with optional query filters
  getTenderSummary: async (queryParams = {}) => {
    try {
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString
        ? `${API_ENDPOINTS.TENDER.LIST}/summary?${queryString}`
        : `${API_ENDPOINTS.TENDER.LIST}/summary`;
      const response = await get(url);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to fetch tender summary',
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch tender summary',
      };
    }
  },

  // Get single tender by ID
  getTenderById: async (id) => {
    try {
      const response = await get(API_ENDPOINTS.TENDER.GET(id));

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to fetch tender details',
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to fetch tender details',
      };
    }
  },

  /**
   * Export a single tender's full detail as a backend-generated Excel
   * (.xlsx) workbook and trigger the browser download.
   */
  exportTenderDetail: async (id, fallbackName = 'Tender') => {
    try {
      if (!id) {
        return { success: false, error: 'Tender ID is required' };
      }

      const blob = await get(`${API_ENDPOINTS.TENDER.GET(id)}/export`, { responseType: 'blob' });

      const sanitized = String(fallbackName || 'Tender')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'Tender';
      const filename = `Tender_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export tender details',
      };
    }
  },

  // Create new tender
  createTender: async (tenderData) => {
    try {
      const response = await post(API_ENDPOINTS.TENDER.CREATE, tenderData);

      if (response.responseCode === 201 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to create tender',
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to create tender',
      };
    }
  },

  // Update tender
  updateTender: async (id, tenderData) => {
    try {
      const response = await put(API_ENDPOINTS.TENDER.UPDATE(id), tenderData);

      if (response.responseCode === 200 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to update tender',
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to update tender',
      };
    }
  },

  // Delete tender
  deleteTender: async (id) => {
    try {
      const response = await del(API_ENDPOINTS.TENDER.DELETE(id));

      if (response.responseCode === 200) {
        return {
          success: true,
        };
      }

      return {
        success: false,
        error: response.responseMessage || 'Failed to delete tender',
      };
    } catch (error) {

      return {
        success: false,
        error: error.message || 'Failed to delete tender',
      };
    }
  },
};

export default tenderService;



